import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  StatusTypeToDisplayNameMapping,
  StepTaskOrInaccessible,
  Task,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {StepTaskSelection} from 'notebook/worksheets/workspace/WorksheetUtils';
import {PMEIProcedureQuery} from 'procedures/worksheets/__generated/PMEIProcedureQuery';
import {
  PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask as PMEIMethodTask,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode_statusChanges_ProcessTaskStatusChangeNode as StatusChangeNode,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_taskTransitionExtensions_TaskTransitionExtensionNode as TaskTransitionExtensionNode,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import {HasAvatar} from 'ui-library/avatar/Avatar';

interface StatusChangeInfo {
  creator?: HasAvatar;
  timestamp: string;
}

function extractStatusChangeInfo(
  statusType: WorksheetTaskStatusType,
  statusChanges: Array<StatusChangeNode>,
  taskTransitionExtensions: Array<TaskTransitionExtensionNode>
): StatusChangeInfo | null {
  const creator = statusChanges.find(
    (statusChange) => statusChange.newStatus.displayName === StatusTypeToDisplayNameMapping[statusType]
  )?.creator;

  const timestamp = taskTransitionExtensions.find(
    (ext) => ext.statusDisplayName === StatusTypeToDisplayNameMapping[statusType]
  )?.timestampUtcIsoformat;

  return timestamp ? {creator, timestamp} : null;
}

export function getProcedureType(pmeis?: Array<PMEIOrInaccessible>): ProcedureType | undefined {
  if (!pmeis || !pmeis[0] || isInaccessibleInfo(pmeis[0])) {
    return undefined;
  }
  const allEqual = pmeis.every(
    (pmei) =>
      !isInaccessibleInfo(pmei) &&
      !isInaccessibleInfo(pmeis[0]) &&
      pmei.procedureRun.procedureType === pmeis[0].procedureRun.procedureType
  );
  assert(allEqual, 'All PMEIs should have the same procedure type');
  return pmeis[0].procedureRun.procedureType;
}

export function extractProcedureRunDataFromTask(
  task?: PMEIMethodTask | null,
  pmei?: PMEIOrInaccessible
): {
  unitOperationName?: string;
  replicateNumber?: number;
  conditionName?: string;
  runStarted?: StatusChangeInfo | null;
  runEnded?: StatusChangeInfo | null;
  procedureType?: ProcedureType;
} {
  if (!task || isInaccessibleInfo(task) || !pmei || isInaccessibleInfo(pmei)) {
    return {};
  }

  const {statusChanges} = task;
  const {planMethodDefinitionVersion, procedureRunConditionReplicate, procedureRun} = pmei;

  if (isInaccessible(planMethodDefinitionVersion)) {
    return {};
  }

  const runStarted = extractStatusChangeInfo(
    WorksheetTaskStatusType.IN_PROGRESS,
    statusChanges,
    pmei.taskTransitionExtensions
  );
  const runEnded = extractStatusChangeInfo(
    WorksheetTaskStatusType.COMPLETED,
    statusChanges,
    pmei.taskTransitionExtensions
  );

  return {
    unitOperationName: planMethodDefinitionVersion.name,
    replicateNumber: procedureRunConditionReplicate.replicateNumber,
    conditionName: procedureRunConditionReplicate.procedureRunCondition.name,
    runStarted,
    runEnded,
    procedureType: procedureRun.procedureType,
  };
}

export function areAllMethodTasksCreated(pmeis: Array<PMEIOrInaccessible>, tasks: Array<Task>): boolean {
  const taskIds = tasks
    .map((task) => (task.__typename === 'ProcessTaskNode' ? task.id : ''))
    .filter((id) => id);
  return (
    (pmeis.length === 0 && tasks.length !== 0) ||
    (pmeis.length === tasks.length &&
      tasks.length !== 0 &&
      pmeis.every(
        (pmei) =>
          pmei.__typename === 'ProcedureMethodExecutionInstanceNode' &&
          pmei.methodTask &&
          pmei.methodTask.__typename === 'ProcessTaskNode' &&
          taskIds.includes(pmei.methodTask.id)
      ))
  );
}

/**
 * In delayed mapping (split pool) scenarios, PMEIs may not have tasks associated with them.
 * To determine readiness, we check that all previous PMEIs' tasks are completed.
 * Returns true if there are no previous PMEIs, or all previous PMEIs' tasks have a terminal status.
 */
export function arePreviousPMEITasksCompleted(pmeis: Array<PMEIOrInaccessible>): boolean {
  const TERMINAL_DISPLAY_NAMES: ReadonlyArray<string> = [
    StatusTypeToDisplayNameMapping[WorksheetTaskStatusType.COMPLETED],
    StatusTypeToDisplayNameMapping[WorksheetTaskStatusType.FAILED],
    StatusTypeToDisplayNameMapping[WorksheetTaskStatusType.SKIPPED],
  ];

  for (const pmei of pmeis) {
    if (isInaccessibleInfo(pmei)) {
      continue;
    }
    const previousPMEIs = pmei.previousProcedureMethodExecutionInstances ?? [];
    for (const prevPmei of previousPMEIs) {
      if (isInaccessibleInfo(prevPmei)) {
        return false;
      }
      const task = prevPmei.methodTask;
      if (!task || isInaccessibleInfo(task)) {
        return false;
      }
      if (!TERMINAL_DISPLAY_NAMES.includes(task.status.displayName)) {
        return false;
      }
    }
  }
  return true;
}

type MethodExecutionInstance = ExtractByTypename<
  PMEIProcedureQuery['procedureMethodExecutionInstance'],
  'ProcedureMethodExecutionInstanceNode'
>['procedureRun']['methodExecutionInstances'][number];

export function getConditionReplicateText(pmei: MethodExecutionInstance): string {
  return `${pmei.procedureRunConditionReplicate.procedureRunCondition.name} - ${pmei.procedureRunConditionReplicate.replicateNumber}`;
}

/**
 * Generates a string describing the conditions being executed in a worksheet from its live
 * method execution instances.
 *
 * - If there is exactly one condition-replicate being executed in the worksheet, returns a label with the condition name and replicate number.
 * - Otherwise, returns a label indicating the number of conditions being executed in the worksheet, which is zero when none are live.
 */
export function getConditionsLabel(
  procedureType: ProcedureType,
  liveMethodExecutionInstances: Array<MethodExecutionInstance>
): string {
  if (liveMethodExecutionInstances.length === 1) {
    return getConditionReplicateText(liveMethodExecutionInstances[0]);
  }

  if (liveMethodExecutionInstances.length > 1) {
    assert(procedureType === ProcedureType.RECIPE, 'Only recipes support parallel execution mode');
  }

  const uniqueConditions = new Set(
    liveMethodExecutionInstances.map((pmei) => pmei.procedureRunConditionReplicate.procedureRunCondition.id)
  );
  return `${uniqueConditions.size} ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: uniqueConditions.size !== 1})}`;
}

/** Whether the step task belongs to a removed condition replicate. */
function isRemovedReplicateStepTask(task: StepTaskOrInaccessible): boolean {
  return (
    !isInaccessible(task) &&
    // Null for tasks that aren't procedure steps, such as those on notebook worksheets.
    task.procedureStepExecutionInstance?.__typename === 'ProcedureStepExecutionInstanceNode' &&
    task.procedureStepExecutionInstance.archiveData.archived
  );
}

/**
 * Selects a step's tasks; transitions skip removed replicates' tasks. Parallel-execution replicates share each
 * step, so only live replicates' tasks set its status; otherwise all of a step's tasks belong to one replicate,
 * which keeps its recorded status after removal.
 */
export function selectStepTasks(
  tasks: Array<StepTaskOrInaccessible>,
  isParallelExecution: boolean
): StepTaskSelection {
  const liveTasks = tasks.filter((task) => !isRemovedReplicateStepTask(task));
  return {statusTasks: isParallelExecution ? liveTasks : tasks, transitionTasks: liveTasks};
}
